import { useState, Fragment, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import AjvErrors from 'ajv-errors';
import Ajv from 'ajv';
import reduce from 'lodash/reduce';

import { CardHeader, CircularProgress, Divider, Grid, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { addSecretUISchema, secretFormSchema, secretTagsSchema } from './form';
import FormModal from '../../common/formModal';

import Styles from '../../theme/commonCss/Styles';

import { palette } from '../../theme/palette';
import SelectComponent from '../../components/Select';
import SecretMetaTags from './common/SecretMetaTags';
import { secretManagerActions } from '../../store/reducers/secret-manager';
import { id } from 'date-fns/locale';

const AddEditSecret = ({
  isOpen,
  secretSelectedData,
  setSecretSelectedData,
  secretTagsData,
  setSecretTagsData,
  isEdit,
  closeModal = () => { },
  templateData,
  secretData,
  hasAccessTo,
  loadingSecrets
}: any) => {
  const themeData: any = useTheme();
  const dispatch = useDispatch();
  const responsiveSize = useMediaQuery('(max-width:900px)');
  const ajv = new Ajv({ allErrors: true });
  AjvErrors(ajv);
  const [ajvErrors, setAjvErrors] = useState<any>({});
  const [isValidName, setIsValidName] = useState<any>({});
  const [isChanged, setIsChanged] = useState<any>(false);
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === 'template_id') {
      setIsChanged(true);
      if (value === '') {
        // Clear out specific values when "Select Template" is selected
        setSecretSelectedData({
          ...secretSelectedData,
          [name]: value,
          key_value_pairs: [], // Clear key-value pairs
        });
        return;
      } else if (value === 'custom') {
        setSecretSelectedData({
          ...secretSelectedData,
          [name]: 0,
          key_value_pairs: [], // Clear key-value pairs
        });
        return;
      }
    }
    setSecretSelectedData({ ...secretSelectedData, [name]: value });

    if (name === 'secret_name' && !isEdit) {
      if (secretData.findIndex((item: any) => item.secret_name.toLowerCase() === value.toLowerCase()) > -1) {
        var obj = {
          ['secret_name']: true,
        };
        setIsValidName(obj);
      } else {
        setIsValidName({});
      }
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    let ajvSchema: any = JSON.parse(JSON.stringify({ ...secretFormSchema }));

    if (secretTagsData.length) {
      ajvSchema = {
        ...secretFormSchema,
        properties: {
          ...secretFormSchema.properties,
          key_value_pairs: {
            type: 'array',
            items: secretTagsData.map((item: any) => secretTagsSchema),
          },
        },
        required: [...secretFormSchema.required, 'key_value_pairs'],
      };
    }

    const validate = ajv.compile(ajvSchema);

    const trimmedsecretSelectedData = reduce(
      secretSelectedData,
      (acc: any, value: any, key: any) => {
        acc[key] = typeof value === 'string' ? value.trim() : value;
        return {
          ...acc,
        };
      },
      {}
    );
    setSecretSelectedData(trimmedsecretSelectedData);

    const trimmedMachineTag = secretTagsData.map((item: any) => {
      return {
        ...item,
        key: typeof item.key === 'string' ? item.key.trim() : item.key,
        value: typeof item.value === 'string' ? item.value.trim() : item.value,
      };
    });

    setSecretTagsData(trimmedMachineTag);

    const reqParams = {
      ...trimmedsecretSelectedData,
      key_value_pairs: trimmedMachineTag,
    };

    //   const output = input.reduce((acc, curr) => {
    //     acc[curr.key] = curr.value;
    //     return acc;
    // }, {});
    if (validate({ ...reqParams })) {

      let finalPaylod = {
        ...trimmedsecretSelectedData,
        key_value_pairs: trimmedMachineTag.reduce((acc: any, curr: any) => {
          acc[curr.key] = curr.value;
          return acc;
        }, {}),
      };

      if (isEdit) {
        dispatch(secretManagerActions.updateSecretRequest(finalPaylod));
      } else {
        if (finalPaylod.template_id == 0) {
          delete finalPaylod["template_id"];
          dispatch(secretManagerActions.addSecretRequest(finalPaylod));
        } else {
          dispatch(secretManagerActions.addSecretRequest(finalPaylod));
        }
      }
      closeModal();
      setAjvErrors({});
    } else {
      if (validate.errors) {
        setAjvErrors(validate.errors[0]);
      }
    }
  };

  const selectedTemplateTags = useMemo(() => {

    if (secretSelectedData.template_id) {

      if (templateData.length) {
        const _selectedTemplate: any = templateData.filter((item: any) => { return item.template_id === secretSelectedData.template_id });
        if (isChanged) {
          if (_selectedTemplate.length) {
            // isEdit
            if (_selectedTemplate[0]['template_tags'] && _selectedTemplate[0]['template_tags'].length) {
              return _selectedTemplate[0]['template_tags'].map((secrets: any, index: number) => { return { ...secrets, id: index + 1, value: '' } })
            }
            return []
          }
        } else {
          return secretSelectedData.key_value_pairs
        }
      }
      return []
    } else {
      return isEdit ? secretTagsData : []; //{ id: 1, key: '', value: '' }
    }
  }, [secretSelectedData])

  return (
    <FormModal
      title={`${isEdit ? 'EDIT ' : 'ADD NEW '} SECRET`}
      isOpen={isOpen}
      handleSubmit={handleSubmit}
      handleClose={closeModal}
      disableOverflow={false}
    >
      {loadingSecrets &&
        <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, backgroundColor: '#fff', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </div>
      }
      <Grid
        container
        spacing={1}
        textAlign={'right'}
        sx={{
          ...Styles.gridContainer,
          '&.MuiGrid-root': {
            paddingBottom: '20px !important',
          },
          marginLeft: 0,
          marginTop: responsiveSize ? '0px' : '12px',
        }}
      >
        {addSecretUISchema.map(({ label, name, required, type }: any) => (
          type === 'input' ?
            <><Grid
              item
              xs={12}
              sm={12}
              md={4}
              sx={{
                ...Styles.gridItem,
                '&.MuiGrid-item': {
                  paddingTop: '0px !important',
                  pl: '0px !important',
                  height: '58px',
                  alignItems: 'center',
                  justifyContent: responsiveSize ? 'flex-start' : 'flex-end',
                },
              }}
            >
              <Typography sx={{ ...Styles.inputLabel, color: themeData.palette.text.primary }} className={required ? 'required-field' : ''}>
                {label}
              </Typography>
            </Grid>
              <Grid item
                xs={12}
                sm={12}
                md={8}
                sx={{
                  '&.MuiGrid-item': {
                    padding: responsiveSize ? '4px 0 0' : '12px 0px',
                    pl: '0px',
                    height: '78px',
                  },
                }}>
                <TextField
                  sx={{
                    '&.MuiTextField-root': {
                      margin: '0px',
                    },
                  }}
                  margin='normal'
                  fullWidth
                  id={name}
                  onChange={handleChange}
                  // onBlur={validateForm}
                  name={name}
                  autoComplete={name}
                  autoFocus={name === 'secret_name' ? true : false}
                  variant='outlined'
                  value={secretSelectedData[name]}
                  size='small'
                  placeholder={label}
                  helperText={
                    name === 'secret_name' ? (
                      isValidName['secret_name'] ? (
                        <p
                          style={{
                            color: palette.error.main,
                            fontSize: '0.75rem',
                            textAlign: 'left',
                            padding: '0',
                            margin: '0',
                            marginLeft: '5px',
                          }}
                        >
                          SECRET NAME already exists! Please specify an alternative.
                        </p>
                      ) : (
                        ajvErrors.instancePath && ajvErrors.instancePath.includes(name) && ajvErrors.message
                      )
                    ) : (
                      ajvErrors.instancePath && ajvErrors.instancePath.includes(name) && ajvErrors.message
                    )
                  }
                  error={
                    name === 'secret_name'
                      ? isValidName['secret_name']
                        ? true
                        : ajvErrors.instancePath && ajvErrors.instancePath.includes(name)
                      : ajvErrors.instancePath && ajvErrors.instancePath.includes(name)
                  }
                />
              </Grid></> : <> <Grid
                item
                xs={12}
                sm={12}
                md={4}
                sx={{
                  ...Styles.gridItem,
                  '&.MuiGrid-item': {
                    paddingTop: '0px !important',
                    pl: '0px !important',
                    alignItems: 'center',
                    height: '58px',
                    justifyContent: responsiveSize ? 'flex-start' : 'flex-end',
                  },
                }}
              >

                <Typography sx={{ ...Styles.inputLabel, color: themeData.palette.text.primary }} className={required ? 'required-field' : ''}>
                  {label}
                </Typography>
              </Grid>
              <Grid item
                xs={12}
                sm={12}
                md={8}
                sx={{
                  '&.MuiGrid-item': {
                    padding: responsiveSize ? '4px 0 0' : '12px 0px',
                    pl: '0px',
                    height: '78px',
                  },
                }}>
                <SelectComponent
                  value={secretSelectedData[name] === 0 ? 'custom' : secretSelectedData[name]}
                  options={[{ label: 'Custom Template', value: 'custom' }, ...templateData.map((item: any) => { return { label: item.template_name, value: item.template_id } })]}
                  onChange={handleChange}
                  name={name}
                  placeholder='Select Template'
                  error={ajvErrors.instancePath && ajvErrors.instancePath.includes('recurs_days')}
                  sx={{
                    width: '100%',
                    textAlign: 'left'
                  }}
                />
                {/* {(isValidName['machine_name'] && name === 'machine_name') && <p style={{ color: '#D81010', fontSize: '0.75rem', textAlign: 'left', padding: '0', margin: '0', marginLeft: '5px' }}>MACHINE NAME already exists!! Please specify alternative "MACHINE NAME". </p>} */}
              </Grid></>
        ))}
      </Grid>

      <CardHeader
        title='Secret Details'
        titleTypographyProps={Styles.title}
      />
      <Divider />
      <SecretMetaTags
        // data={secretSelectedData.template_id ? templateData.length ? templateData.filter((item:any)=>{return item.template_id===secretSelectedData.template_id})[0]['tags'].map((secrets:any,index:number)=>{return{...secrets,id:index+1, value:'' }}):[]:[]}
        selectedSecretId={secretSelectedData.secret_id}
        data={(secretSelectedData.template_id || secretSelectedData.template_id === 0) ? selectedTemplateTags : secretSelectedData.key_value_pairs}
        setData={setSecretTagsData}
        template_id={secretSelectedData.template_id}
        isFromSecrets={true}
        overlayMsg={'Please fill in all required fields to add a new secret.'}
        hasAccessTo={isEdit ? hasAccessTo : { ...hasAccessTo, delete: true }}
        error={ajvErrors.instancePath && ajvErrors.instancePath.includes('key_value_pairs')}
      />
    </FormModal>
  );
};

export default AddEditSecret;
